// How a response's fields read, shared by the Responses table and the
// response panel.

import { Badge } from "@pej/kore/components";
import type { AnswerCell, StepRow } from "@/api/types";
import { multiLabels, optionMeta } from "@/features/dashboard/stats-mapping";
import { builtinIconSvg } from "@/features/surveys/choice-icons";

export function formatSubmittedAt(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

export function ScoreBadge({
    score,
    meta,
}: {
    score: number | null;
    meta: Map<number, { label: string; icon: string | null }>;
}) {
    if (score === null) return <Badge tone="neutral">—</Badge>;
    const info = meta.get(score);
    const icon = info?.icon ? builtinIconSvg(info.icon) : undefined;
    return (
        <Badge tone="neutral">
            {icon && (
                <span
                    aria-hidden="true"
                    className="[&>svg]:size-3"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: static, module-authored SVG shapes (choice-icons.ts) — no user input reaches this markup.
                    dangerouslySetInnerHTML={{ __html: icon }}
                />
            )}
            {info?.label ?? score}
        </Badge>
    );
}

/** A step's answers as words: an option's label, a multi's labels
 *  comma-separated, or the text; null for no answer (or an empty multi).
 *  Labels are looked up once per step, not per cell. */
export function answerFormatter(step: StepRow): (answer: AnswerCell | undefined) => string | null {
    if (step.kind === "choice") {
        const labels = new Map([...optionMeta(step)].map(([key, meta]) => [key, meta.label] as const));
        return (answer) => (answer?.optionKey ? (labels.get(answer.optionKey) ?? answer.optionKey) : null);
    }
    if (step.kind === "multi") {
        const labels = multiLabels(step);
        return (answer) => {
            const keys = answer?.optionKeys ?? [];
            return keys.length > 0 ? keys.map((key) => labels.get(key) ?? key).join(", ") : null;
        };
    }
    return (answer) => answer?.text || null;
}
